import { useEffect } from 'react';
import { animate } from 'framer-motion';

const HEADER_OFFSET = 76;

export function useSmoothAnchorScroll() {
  useEffect(() => {
    let controls: ReturnType<typeof animate> | null = null;

    const stop = () => {
      controls?.stop();
      controls = null;
    };

    const handleClick = (event: MouseEvent) => {
      if (event.defaultPrevented || event.button !== 0 || event.metaKey || event.ctrlKey || event.shiftKey) return;
      const link = (event.target as HTMLElement).closest<HTMLAnchorElement>('a[href^="#"]');
      if (!link) return;

      const hash = link.getAttribute('href') ?? '';
      const target = hash === '#top' || hash === '#' ? null : document.querySelector<HTMLElement>(hash);
      if (hash !== '#top' && hash !== '#' && !target) return;

      event.preventDefault();
      const destination = target
        ? Math.max(0, target.getBoundingClientRect().top + window.scrollY - HEADER_OFFSET)
        : 0;

      stop();
      const prefersReduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const distance = Math.abs(destination - window.scrollY);

      if (prefersReduced || distance < 2) {
        window.scrollTo(0, destination);
      } else {
        controls = animate(window.scrollY, destination, {
          duration: Math.min(1, Math.max(0.45, distance / 3000)),
          ease: [0.65, 0, 0.35, 1],
          onUpdate: (value) => window.scrollTo(0, value),
          onComplete: () => {
            controls = null;
          },
        });
      }

      history.replaceState(null, '', hash);
      if (target) {
        target.setAttribute('tabindex', '-1');
        target.focus({ preventScroll: true });
      }
    };

    document.addEventListener('click', handleClick);
    window.addEventListener('wheel', stop, { passive: true });
    window.addEventListener('touchstart', stop, { passive: true });
    window.addEventListener('keydown', stop);

    return () => {
      stop();
      document.removeEventListener('click', handleClick);
      window.removeEventListener('wheel', stop);
      window.removeEventListener('touchstart', stop);
      window.removeEventListener('keydown', stop);
    };
  }, []);
}
